import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { FeeCard } from './parent-fees.component';
import { FeesListBody, GetRedirectionUrlBody, OtherFeeTerm, ParentFeesService, PaymentGateway, PaymentLineItem, TermFee, TypeFee } from './parent-fees.service';

declare var Razorpay: any;

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 60; // 5 minutes at 5s each, per the API reference

export type PaymentStage = 'gateway' | 'submitting' | 'razorpay' | 'polling' | 'instamojo' | 'success' | 'failed';

// Payment initiation (PaymentDetails.js) — gateway selection, then one of
// three flows keyed off the payment-init response's body.slug:
//   "razorpay"  -> Razorpay Checkout.js (this app's web equivalent of the
//                  mobile app's native RazorpayCheckout SDK), backend-
//                  verified synchronously — the only path with a reliable
//                  confirmation.
//   "instamojo" -> the mobile app detects completion by matching a WebView
//                  page title, which has no web equivalent; here it's
//                  simply opened in a new tab with no automatic completion
//                  detection, matching Instamojo's own hosted checkout
//                  flow returning the user to a redirect URL on completion.
//   anything else -> generic gateway: open redirectURL in a new tab, then
//                  poll getPaymentStatus every 5s for up to 5 minutes.
// Rendered as a modal from ParentFeesComponent's detail screen's Pay button.
@Component({
  selector: 'app-parent-fees-payment',
  templateUrl: './parent-fees-payment.component.html',
  styleUrls: ['./parent-fees-payment.component.css'],
})
export class ParentFeesPaymentComponent implements OnInit, OnDestroy {

  @Input() cards: FeeCard[] = [];
  @Input() studentId = '';
  @Input() classId = '';
  @Input() academicId = '';
  @Input() studentName = '';
  @Output() closed = new EventEmitter<void>();
  @Output() paid = new EventEmitter<PaymentStage>();

  stage: PaymentStage = 'gateway';
  gateways: PaymentGateway[] = [];
  gatewaysLoading = false;
  gatewaysError = '';
  selectedGatewayId: number | null = null;
  email = '';
  submitError = '';
  submitting = false;

  successReferenceId = '';
  failedReason = '';

  private pollTimer: any;
  private pollAttempts = 0;
  private pollPaymentRequestId = '';

  constructor(private feesService: ParentFeesService, private zone: NgZone) { }

  ngOnInit(): void {
    this.loadGateways();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private get lang(): string {
    // Deliberately always 'en', not the UI's selected display language —
    // the parent mobile app's backend endpoints expect this constant
    // regardless of what language the user has the interface in; switching
    // the UI language must never change this API payload field.
    return 'en';
  }

  // Grand total actually charged: Σ every line item's balance + late fee,
  // computed fresh at send time (never carried forward from an earlier
  // screen's stale calculation) — per the payload spec this was built from.
  get totalAmount(): number {
    return this.cards.reduce((sum, c) => sum + c.balance + (c.lateFee > 0 ? c.lateFee : 0), 0);
  }

  // Top-level totalLateFee — CONFIRMED against the real backend (a
  // "correct" Σ(late fee) value, per an earlier abstract payload spec, was
  // tried here and the backend rejected it with "This fee amount could not
  // be verified" for a bus fee that pays fine on mobile with the exact same
  // source data). Reverted to matching mobile's own actual value exactly:
  // Σ(balance), i.e. the selected amount EXCLUDING late fee — despite the
  // misleading field name, this is what the backend's verification check
  // actually expects here. Do not "fix" this again without re-confirming
  // against a real successful payment first.
  get totalLateFeeAmount(): number {
    return this.cards.reduce((sum, c) => sum + c.balance, 0);
  }

  // Server-driven cooldown (timeLeftToPayAgain, per fee, from getFeesList —
  // see parent-fees.component.ts). Purely reactive to whatever the fee list
  // last fetched, no client-side countdown timer. Normally a blocked fee's
  // checkbox is hidden entirely so it can't reach this modal at all, but
  // this is a defensive fallback in case a card's lockout starts between
  // selecting it and opening this modal (e.g. the parent selected it just
  // before it entered cooldown from a payment made on another device/tab).
  get blockedTimeLabel(): string | null {
    const blocked = this.cards.find(c => c.timeLeftToPayAgain);
    return blocked ? blocked.timeLeftToPayAgain : null;
  }

  // Deliberately the real selected fee name(s), not the mobile app's
  // hardcoded "Total amount" (a known bug flagged in the API reference).
  get paymentDescription(): string {
    if (this.cards.length === 1) { return this.cards[0].name; }
    return this.cards.map(c => c.name).join(', ');
  }

  // Top-level fkLateFeeId, per the payload spec this was built from: only
  // meaningful when exactly one late fee applies across the whole request
  // (send null otherwise) — NOT the mobile app's buggy "whichever item was
  // iterated last" value. Each array entry's own fkLateFeeId (see
  // buildLineItem) is what the backend actually charges against regardless;
  // this top-level field is just for the single-fee case where it's
  // unambiguous.
  private get topLevelLateFeeId(): number | null {
    const ids = new Set(
      this.cards
        .filter(c => c.lateFee > 0)
        .map(c => (c.raw as TermFee | TypeFee | OtherFeeTerm).pkLateFeesId ?? null)
        .filter((id): id is number => id !== null)
    );
    return ids.size === 1 ? [...ids][0] : null;
  }

  loadGateways(): void {
    this.gatewaysLoading = true;
    this.gatewaysError = '';
    this.feesService.getPaymentGateway(this.classId, this.academicId, this.lang).subscribe({
      next: (res: any) => {
        this.gatewaysLoading = false;
        const list: PaymentGateway[] = (res && res.status && res.body) || [];
        this.gateways = list;
        if (list.length === 1) {
          this.selectedGatewayId = list[0].pkPaymentGatewayId;
        }
        if (!list.length) {
          this.gatewaysError = 'No payment gateway is configured for this school.';
        }
      },
      error: () => {
        this.gatewaysLoading = false;
        this.gatewaysError = 'Unable to load payment options.';
      },
    });
  }

  // paid.emit() fires here — when the user dismisses this modal — rather
  // than the instant verification/polling succeeds. Emitting it immediately
  // on success (an earlier version of this code did) made the parent set
  // paymentCard = null right away, which destroys this whole component
  // (it's rendered via *ngIf="paymentCard as pc") before the "Payment
  // Successful" screen the user is meant to see even had a chance to
  // render — visibly, the modal just looked stuck on "Processing
  // payment..." forever, since success/paid.emit() happened in the same
  // tick and the destroy won the race.
  //
  // Fired for every terminal stage, not just 'success': the mobile app
  // itself never reliably tells the Fees list to refetch on return from ANY
  // of its 3 payment paths (a known gap flagged in the API reference this
  // was built from) — most notably Instamojo, which has no backend
  // verification at all, so 'instamojo' here is exactly the case where the
  // parent genuinely can't know the outcome without just re-checking. A
  // stale refetch after 'failed'/'gateway'/'submitting' is harmless, so
  // refreshing unconditionally (rather than only on confirmed success) gives
  // the best chance of an up-to-date balance without a manual refresh.
  close(): void {
    this.stopPolling();
    this.paid.emit(this.stage);
    this.closed.emit();
  }

  // Instamojo has no backend hook this app can poll (its own API response
  // never returns a paymentRequestId — confirmed against the API
  // reference) and no way to inspect a separate browser tab's page title
  // like the mobile app's in-app WebView does, so there's no honest way to
  // auto-detect success or failure here. Rather than guess, this just asks
  // the user and, either way, closes and refreshes the fee list (via
  // close()'s paid.emit(this.stage) — 'instamojo' is one of the stages
  // ParentFeesComponent.onPaid() already reloads on) so they see the real,
  // current balance instead of a stale screen.
  confirmInstamojoDone(): void {
    this.close();
  }

  private buildLineItem(card: FeeCard): PaymentLineItem {
    const f = card.raw as TermFee | TypeFee | OtherFeeTerm;
    const base = {
      // amount here is the fee's own balance, not totalAmount (balance +
      // late fee) — totalAmount is only the top-level payload field.
      amount: card.balance,
      totalLateFee: card.lateFee || 0,
      fkLateFeeId: f.pkLateFeesId ?? null,
      discount: card.discount || 0,
      fkFeeStructuresId: f.fkFeeStructuresId ?? null,
    };
    if (card.kind === 'term') {
      const tf = card.raw as TermFee;
      return { ...base, isTypeOrTerm: 'TERM', termId: tf.fkFeesTermId };
    }
    if (card.kind === 'type') {
      const tf = card.raw as TypeFee;
      const isBusFee = !!tf.isBusFee;
      return {
        ...base, isTypeOrTerm: 'TYPE',
        // feesTypeId is null when this is a bus fee (confirmed against the
        // real payload shape) — the bus fields identify it instead.
        feesTypeId: isBusFee ? null : tf.fkFeesTypeId,
        // The response's isBusFee is a 0/1 number (confirmed against a real
        // getFeesList response), but the payment-initiation request sample
        // shows a real boolean — coerce it here rather than pass the
        // response's own number type through untouched.
        isBusFee, busId: isBusFee ? tf.busId : null,
        busTermId: isBusFee ? (tf.busTermId ?? null) : null,
        busStopId: isBusFee ? (tf.busStopId ?? null) : null,
      };
    }
    const of = card.raw as OtherFeeTerm;
    return { ...base, isTypeOrTerm: 'OTHER', pkOtherFeesId: of.pkOtherFeesId, pkOtherFeeTermId: of.pkOtherFeeTermId };
  }

  // isPaymentInProgressRef-equivalent guard, in code rather than relying
  // solely on the button's [disabled] binding — belt-and-braces against a
  // double submission (e.g. a fast double-click before Angular re-renders
  // the disabled state).
  submitPayment(): void {
    if (this.submitting) { return; }
    this.submitError = '';
    if (this.blockedTimeLabel) { return; }
    if (!this.selectedGatewayId) {
      this.submitError = 'Please select a payment gateway.';
      return;
    }
    if (!this.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim())) {
      this.submitError = 'Please enter a valid email address.';
      return;
    }

    this.stage = 'submitting';
    this.submitting = true;

    // Refresh balances/ids right before charging — the cards this modal
    // opened with were fetched whenever the list last loaded, and can go
    // stale (another payment in progress, a discount or late fee applied,
    // a fee structure change) by the time the user actually taps Pay. The
    // backend's own "amount could not be verified" error is exactly this
    // staleness check catching a mismatch (confirmed against the API
    // reference this was built from) — the mobile app's own fix is
    // "pull to refresh and retry," so this does that refresh automatically
    // instead of making the user hit a cryptic error first.
    this.feesService.getFeesList(this.classId, this.studentId, this.academicId).subscribe({
      next: (res: any) => {
        const body: FeesListBody | null = res && Array.isArray(res.termFees) ? res
          : (res && res.status && res.body) ? res.body : null;
        if (body) {
          this.cards = this.cards.map(c => this.refreshCard(c, body));
        }
        this.doSubmitPayment();
      },
      // Refresh is best-effort — if it fails, fall back to submitting with
      // whatever data this modal already had rather than blocking payment
      // entirely on a transient network error.
      error: () => this.doSubmitPayment(),
    });
  }

  // Re-points a card's raw fee record (and the balance/discount/lateFee it
  // displays) at the freshly-fetched matching record, found by the same id
  // each kind is keyed by elsewhere in this feature (fkFeesTermId /
  // fkFeesTypeId / pkOtherFeesId+pkOtherFeeTermId). Falls back to the
  // original card unchanged if the fee has disappeared from the fresh
  // response (fully paid off elsewhere, removed, etc.) — the actual
  // request will then simply fail against the current amount, same as
  // today, rather than this method guessing.
  private refreshCard(card: FeeCard, body: FeesListBody): FeeCard {
    if (card.kind === 'term') {
      const raw = card.raw as TermFee;
      const fresh = (body.termFees || []).find(f => f.fkFeesTermId === raw.fkFeesTermId);
      if (!fresh) { return card; }
      return { ...card, balance: fresh.Balance, discount: fresh.discount || 0, lateFee: fresh.lateFee || 0, raw: fresh };
    }
    if (card.kind === 'type') {
      const raw = card.raw as TypeFee;
      // Bus fees can have a null/undefined fkFeesTypeId (see the matching
      // fix in parent-fees.component.ts's card key), so matching by that
      // field alone would collide with every OTHER bus fee that also has a
      // null fkFeesTypeId and silently refresh against the wrong record
      // (e.g. one that's already fully paid — Balance: 0 — sending a
      // zeroed-out amount to the backend). Match bus fees by
      // busTermId/busStopId instead, same as the card's own selection key.
      const fresh = (body.typeFees || []).find(f => raw.isBusFee
        ? (f.isBusFee && f.busTermId === raw.busTermId && f.busStopId === raw.busStopId)
        : f.fkFeesTypeId === raw.fkFeesTypeId);
      if (!fresh) { return card; }
      return { ...card, balance: fresh.Balance, discount: fresh.Discount || 0, lateFee: fresh.lateFee || 0, raw: fresh };
    }
    const raw = card.raw as OtherFeeTerm;
    const fresh = (body.otherFeeTerms || []).find(f => f.pkOtherFeesId === raw.pkOtherFeesId && f.pkOtherFeeTermId === raw.pkOtherFeeTermId);
    if (!fresh) { return card; }
    return { ...card, balance: fresh.Balance, discount: fresh.Discount || 0, lateFee: fresh.lateFee || 0, raw: fresh };
  }

  private doSubmitPayment(): void {
    // Split every selected fee across the 3 arrays by kind — continueToPay
    // in the mobile app sends everything currently selected in ONE combined
    // request/transaction, not one call per fee (per the API reference).
    // Arrays not applicable to the current selection are sent empty.
    const body: GetRedirectionUrlBody = {
      fkClassId: this.classId,
      emailId: this.email.trim(),
      feesTermArray: this.cards.filter(c => c.kind === 'term').map(c => this.buildLineItem(c)),
      feesTypeArray: this.cards.filter(c => c.kind === 'type').map(c => this.buildLineItem(c)),
      otherFeesArray: this.cards.filter(c => c.kind === 'other').map(c => this.buildLineItem(c)),
      feeType: 'schoolFees',
      totalAmount: this.totalAmount,
      totalLateFee: this.totalLateFeeAmount,
      fkStudentId: this.studentId,
      fkPaymentGatewayId: this.selectedGatewayId!,
      lang: this.lang,
      fkLateFeeId: this.topLevelLateFeeId,
    };

    // TEMP DEBUG — remove once the "could not be verified" bus-fee issue is
    // root-caused. Logs the exact payload being sent, since copying it from
    // the Network tab hasn't been reliable so far.
    console.log('[fee payment debug] request body:', JSON.parse(JSON.stringify(body)));

    this.feesService.getRedirectionUrl(body).subscribe({
      next: (res: any) => {
        this.submitting = false;
        if (!res || !res.status || !res.body) {
          this.stage = 'gateway';
          this.submitError = (res && res.message) || 'Unable to initiate payment.';
          return;
        }
        this.routeBySlug(res.body);
      },
      error: (err) => {
        this.submitting = false;
        this.stage = 'gateway';
        this.submitError = (err && err.error && err.error.message) || 'Unable to initiate payment.';
      },
    });
  }

  private routeBySlug(body: any): void {
    if (body.slug === 'razorpay') {
      this.stage = 'razorpay';
      this.openRazorpay(body);
    } else if (body.slug === 'instamojo') {
      this.stage = 'instamojo';
      window.open(body.redirectURL, '_blank', 'noopener');
    } else {
      this.stage = 'polling';
      this.pollPaymentRequestId = body.paymentRequestId;
      window.open(body.redirectURL, '_blank', 'noopener');
      this.startPolling();
    }
  }

  // Loads Razorpay's Checkout.js on demand (not unconditionally for every
  // visitor) — the web equivalent of the mobile app's native
  // RazorpayCheckout SDK. description is deliberately the fee's own name,
  // not the mobile app's hardcoded "Total amount" (a known bug flagged in
  // the API reference this was built from).
  //
  // Every Razorpay callback (handler/ondismiss/payment.failed) runs OUTSIDE
  // Angular's zone, since Checkout.js is a plain third-party <script>, not
  // something Angular's zone.js patches — without zone.run(), setting
  // this.stage inside them updates the component instance but never
  // triggers change detection, so the modal visibly just sits on
  // "Processing payment..." forever even though the payment actually
  // succeeded or failed. This was the actual bug behind "no success/failure
  // screen appears after payment."
  private openRazorpay(body: any): void {
    this.loadRazorpayScript().then(() => {
      const options = {
        key: body.clientId,
        order_id: body.orderId,
        name: this.studentName || 'Fee Payment',
        description: this.paymentDescription,
        theme: { color: getComputedStyle(document.documentElement).getPropertyValue('--brand-primary-color').trim() || '#158ed2' },
        handler: (response: any) => this.zone.run(() => this.verifyRazorpay(response)),
        modal: {
          ondismiss: () => this.zone.run(() => {
            this.stage = 'failed';
            this.failedReason = 'Payment was cancelled.';
          }),
        },
      };
      const rzp = new Razorpay(options);
      rzp.on('payment.failed', () => this.zone.run(() => {
        this.stage = 'failed';
        this.failedReason = 'Payment failed.';
      }));
      rzp.open();
    }).catch(() => {
      this.zone.run(() => {
        this.stage = 'failed';
        this.failedReason = 'Unable to load the payment gateway.';
      });
    });
  }

  private loadRazorpayScript(): Promise<void> {
    if (typeof Razorpay !== 'undefined') { return Promise.resolve(); }
    return new Promise((resolve, reject) => {
      const existing = document.getElementById('razorpay-checkout-js');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject());
        return;
      }
      const script = document.createElement('script');
      script.id = 'razorpay-checkout-js';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.body.appendChild(script);
    });
  }

  private verifyRazorpay(response: any): void {
    this.feesService.verifyRazorpayPayment(
      response.razorpay_payment_id, response.razorpay_order_id, response.razorpay_signature
    ).subscribe({
      next: (res: any) => {
        if (res && res.status) {
          this.successReferenceId = response.razorpay_payment_id;
          this.stage = 'success';
        } else {
          this.stage = 'failed';
          this.failedReason = 'Payment could not be verified.';
        }
      },
      error: () => {
        this.stage = 'failed';
        this.failedReason = 'Payment could not be verified.';
      },
    });
  }

  // Generic-gateway path: the mobile app's own poll-result handling is
  // dead code (success/failure navigation is commented out there — see the
  // API reference), so only a definitive 200/402 status_code here, a
  // manual cancel, or the 5-minute timeout ever resolves this stage.
  private startPolling(): void {
    this.pollAttempts = 0;
    this.pollTimer = setInterval(() => this.pollOnce(), POLL_INTERVAL_MS);
  }

  private pollOnce(): void {
    this.pollAttempts++;
    if (this.pollAttempts > POLL_MAX_ATTEMPTS) {
      this.stopPolling();
      this.stage = 'failed';
      this.failedReason = 'Payment confirmation timed out.';
      return;
    }
    this.feesService.getPaymentStatus(this.pollPaymentRequestId, this.studentId, this.classId, this.lang).subscribe({
      next: (res: any) => {
        if (res && res.status_code === 200) {
          this.stopPolling();
          this.successReferenceId = res?.body?.referenceId || '';
          this.stage = 'success';
        } else if (res && res.status_code === 402) {
          this.stopPolling();
          this.stage = 'failed';
          this.failedReason = 'Payment failed.';
        }
        // Any other response: keep polling until success/failure/timeout.
      },
      error: () => { /* transient poll errors are ignored; timeout still applies */ },
    });
  }

  cancelPolling(): void {
    this.stopPolling();
    this.stage = 'failed';
    this.failedReason = 'Payment cancelled.';
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
